import { Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { AuthStore } from '@/store/auth.store';
import { UserStore } from '@/store/user.store';
import { RoleStore } from '@/store/role.store';
import { SidebarUiStore } from '@/store/sidebar-ui.store';
import type { Role } from '@/models/role.model';
import {
  ADMIN_EXCLUSIVE_NAV_ITEMS,
  GUEST_MENU_ITEMS,
  USER_MENU_ITEMS,
  CONSULTANT_MENU_ITEMS,
  MODERATOR_MENU_ITEMS,
  ADMIN_MENU_ITEMS,
  isPublicBlogNavItem,
} from './sidebar-nav.config';
import { BlogNavStore } from '@/store/blog-nav.store';
import { DashboardStore } from '@/pages/dashboard/dashboard.store';
import { UserProfileLinkComponent } from './user-profile-link/user-profile-link.component';
import { RoleSelectorComponent } from './role-selector/role-selector.component';
import { SidebarNavItemComponent } from './sidebar-nav-item/sidebar-nav-item.component';

@Component({
  selector: 'app-sidebar-nav',
  standalone: true,
  imports: [
    LucideAngularModule,
    UserProfileLinkComponent,
    RoleSelectorComponent,
    SidebarNavItemComponent,
  ],
  templateUrl: './sidebar-nav.component.html',
})
export class SidebarNavComponent {
  readonly #router = inject(Router);
  readonly authStore = inject(AuthStore);
  readonly userStore = inject(UserStore);
  readonly roleStore = inject(RoleStore);
  readonly sidebarUiStore = inject(SidebarUiStore);
  readonly #blogNavStore = inject(BlogNavStore);
  readonly #dashboardStore = inject(DashboardStore);

  user = this.userStore.user;
  activeRole = this.roleStore.activeRole;
  switchableRoles = this.roleStore.switchableRoles;

  readonly visibleGuestMenuItems = computed(() => this.#filterPublicBlogItem(GUEST_MENU_ITEMS));

  visibleMenuItems = computed(() => {
    const role = this.roleStore.activeRole();
    const assigned = this.roleStore.assignedRoles();
    let items: typeof GUEST_MENU_ITEMS;
    switch (role) {
      case 'consultant':
        items = CONSULTANT_MENU_ITEMS;
        break;
      case 'moderator':
        items = assigned.includes('admin')
          ? [...MODERATOR_MENU_ITEMS, ...ADMIN_EXCLUSIVE_NAV_ITEMS]
          : MODERATOR_MENU_ITEMS;
        break;
      case 'admin':
        items = ADMIN_MENU_ITEMS;
        break;
      default:
        items = USER_MENU_ITEMS;
    }
    return this.#filterPublicBlogItem(items);
  });

  #filterPublicBlogItem(items: typeof GUEST_MENU_ITEMS): typeof GUEST_MENU_ITEMS {
    if (this.#blogNavStore.hasPublishedBlogContent()) {
      return items;
    }
    return items.filter((item) => !isPublicBlogNavItem(item));
  }

  closeSidebar(): void {
    this.sidebarUiStore.close();
  }

  onRoleChange(role: Role): void {
    // Defer to next tick to avoid NG0100 (ExpressionChangedAfterItHasBeenCheckedError)
    // when visibleMenuItems recomputes and sidebar items re-render in the same CD cycle
    setTimeout(() => this.roleStore.setActiveRole(role), 0);
  }

  logout(): void {
    this.userStore.clear();
    this.#dashboardStore.clear();
    this.authStore.logout().then(() => this.#router.navigate(['/']));
  }
}
